import { toast } from "react-toastify";
import axiosInstance from "../../../../axiosInstance";
import { HRMS_ATS_API_URL } from "../../../Constants/baseConstant";
import { getDomain, getUserData, s3Client } from "../../Base/helper/baseFunctions";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { GetObjectCommand } from "@aws-sdk/client-s3";
import NProgress from "nprogress";

// ── Get all agreements ────────────────────────────────────────────────────────
export const GetAtsAgreementsHelper = async (params) => {
    const user = getUserData();
    NProgress.start();
    return await axiosInstance("/ats/getAgreements", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            domain_name: getDomain(),
            client_id: params?.client_id || null,
            status: params?.status || null,
        },
    })
        .then((res) => {
            NProgress.done();
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            NProgress.done();
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

// ── Add agreement ─────────────────────────────────────────────────────────────
export const AddAtsAgreementHelper = async (params) => {
    const user = getUserData();
    toast.dismiss();
    toast.info("Saving...");
    return await axiosInstance("/ats/addAgreement", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            domain_name: getDomain(),
            client_id: params?.client_id,
            client_name: params?.client_name || null,
            agreement_type: params?.agreement_type,
            start_date: params?.start_date,
            end_date: params?.end_date || null,
            value: params?.value || null,
            commission_pct: params?.commission_pct || null,
            notes: params?.notes || null,
            status: params?.status || "Active",
            created_by: user?.ats_user_id,
            template_responses: params?.template_responses || null,
        },
    })
        .then((res) => {
            toast.dismiss();
            toast.success("Agreement added successfully");
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            toast.dismiss();
            toast.error(err?.response?.data?.error?.[0]?.message || "Failed to add agreement");
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

// ── Update agreement ──────────────────────────────────────────────────────────
export const UpdateAtsAgreementHelper = async (params) => {
    const user = getUserData();
    toast.dismiss();
    toast.info("Saving...");
    return await axiosInstance("/ats/updateAgreement", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            domain_name: getDomain(),
            agreement_id: params?.agreement_id,
            agreement_type: params?.agreement_type,
            start_date: params?.start_date,
            end_date: params?.end_date || null,
            value: params?.value || null,
            commission_pct: params?.commission_pct || null,
            status: params?.status,
            notes: params?.notes || null,
            updated_by: user?.ats_user_id,
            template_responses: params?.template_responses || null,
        },
    })
        .then((res) => {
            toast.dismiss();
            toast.success("Agreement updated successfully");
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            toast.dismiss();
            toast.error(err?.response?.data?.error?.[0]?.message || "Failed to update agreement");
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

// ── Delete agreement ──────────────────────────────────────────────────────────
export const DeleteAtsAgreementHelper = async (params) => {
    const user = getUserData();
    toast.dismiss();
    toast.info("Deleting...");
    return await axiosInstance("/ats/deleteAgreement", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            domain_name: getDomain(),
            agreement_id: params?.agreement_id,
        },
    })
        .then((res) => {
            toast.dismiss();
            toast.success("Agreement deleted successfully");
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            toast.dismiss();
            toast.error(err?.response?.data?.error?.[0]?.message || "Failed to delete agreement");
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

// ── Upload signed copy ────────────────────────────────────────────────────────
export const UploadAgreementSignedCopyHelper = async (params) => {
    const user = getUserData();
    toast.dismiss();
    toast.info("Uploading signed copy...");
    return await axiosInstance("/ats/uploadAgreementSignedCopy", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            domain_name: getDomain(),
            agreement_id: params?.agreement_id,
            file_b64: params?.file_b64,
            file_name: params?.file_name,
        },
    })
        .then((res) => {
            toast.dismiss();
            toast.success("Signed copy uploaded successfully");
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            toast.dismiss();
            toast.error(err?.response?.data?.error?.[0]?.message || "Failed to upload signed copy");
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

// ── Generate agreement DOCX and trigger browser download ─────────────────────
export const GenerateAgreementDocHelper = async (params) => {
    const user = getUserData();
    NProgress.start();
    try {
        const res = await axiosInstance("/ats/generateAgreementDoc", {
            method: "POST",
            baseURL: HRMS_ATS_API_URL,
            responseType: "blob",
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem("hrmsbearertoken")}`
            },
            data: {
                domain_name: getDomain(),
                ...params,
            },
        });

        // Build a safe filename — prefer Content-Disposition, fall back to params
        const disposition = res.headers?.["content-disposition"] || "";
        const headerName = disposition.match(/filename="?([^";\n]+)"?/)?.[1]?.trim();
        const safeClient = (params?.client_name || "Client").replace(/[^a-zA-Z0-9]/g, "_");
        const safeDate = (params?.start_date || new Date().toISOString().slice(0, 10));
        const filename = (headerName && headerName.endsWith(".docx"))
            ? headerName
            : `Agreement_Draft_${safeClient}_${safeDate}.docx`;

        // res.data is already a Blob (responseType: "blob") with the server's MIME type —
        // use it directly; do not re-wrap, which would strip the type.
        const url = window.URL.createObjectURL(res.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        NProgress.done();
        toast.success("Agreement draft downloaded");
        return { message: "Success", hasError: false };
    } catch (err) {
        NProgress.done();
        toast.error("Failed to generate agreement draft");
        return { message: "Error", hasError: true, data: err?.response?.data };
    }
};

// ── Get pre-signed URL for viewing a signed copy ─────────────────────────────
export const GetAgreementSignedUrlHelper = async (params) => {
    try {
        NProgress.start();
        const bucketParams = {
            Bucket: process.env.REACT_APP_DO_BUCKET_NAME,
            Key: params?.signed_copy_path,
        };
        const url = await getSignedUrl(s3Client, new GetObjectCommand(bucketParams), { expiresIn: 600 });
        NProgress.done();
        return { message: "Success", hasError: false, data: { data: { url } } };
    } catch (err) {
        NProgress.done();
        toast.error("Failed to retrieve signed copy URL");
        return { message: "Error", hasError: true, data: err };
    }
};

// ── Fee Override Approval Workflow ───────────────────────────────────────────

// Employee submits a custom-fee override request → admins get a MyTask notification
export const RequestFeeOverrideHelper = async (params) => {
    const user = getUserData();
    return await axiosInstance("/ats/requestFeeOverride", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            domain_name: getDomain(),
            requester_name: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.username || "Employee",
            requester_emp_id: user?.employee_id || user?.emp_users_pk,
            client_id: params?.client_id,
            client_name: params?.client_name,
            commission_pct: params?.commission_pct,
            fee_basis: params?.fee_basis,
            start_date: params?.start_date,
            end_date: params?.end_date || null,
            replacement_months: params?.replacement_months,
            notes: params?.notes || null,
            authority_name: params?.authority_name,
            authority_title: params?.authority_title,
            period_months: params?.period_months,
            org_name: params?.org_name || null,
            org_address: params?.org_address || null,
        },
    })
        .then((res) => ({ message: "Success", hasError: false, data: res?.data }))
        .catch((err) => ({ message: "Error", hasError: true, data: err?.response?.data }));
};

// Admin fetches all pending requests for their workspace; employee polls their own request status by request_id
export const GetFeeOverrideRequestsHelper = async ({ request_id, client_id } = {}) => {
    const user = getUserData();
    return await axiosInstance("/ats/getFeeOverrideRequests", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            domain_name: getDomain(),
            request_id: request_id || null,
            client_id: client_id || null,
        },
    })
        .then((res) => ({ message: "Success", hasError: false, data: res?.data }))
        .catch((err) => ({ message: "Error", hasError: true, data: err?.response?.data }));
};

// Admin approves or denies a pending override request
export const RespondFeeOverrideHelper = async ({ request_id, action }) => {
    const user = getUserData();
    return await axiosInstance("/ats/respondFeeOverride", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            domain_name: getDomain(),
            request_id,
            action,   // "approved" | "denied"
            responded_by: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Admin",
        },
    })
        .then((res) => ({ message: "Success", hasError: false, data: res?.data }))
        .catch((err) => ({ message: "Error", hasError: true, data: err?.response?.data }));
};

// ── Agreement Templates Helpers ──────────────────────────────────────────────
export const GetAtsAgreementTemplatesHelper = async () => {
    NProgress.start();
    return await axiosInstance("/ats/getAgreementTemplates", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            workspace: getDomain(),
        },
    })
        .then((res) => {
            NProgress.done();
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            NProgress.done();
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

export const SaveAtsAgreementTemplateHelper = async (params) => {
    const user = getUserData();
    NProgress.start();
    return await axiosInstance("/ats/saveAgreementTemplate", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            workspace: getDomain(),
            user_id: user?.id || null,
            created_by: user?.id || null,
            ...params,
        },
    })
        .then((res) => {
            NProgress.done();
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            NProgress.done();
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

export const DeleteAtsAgreementTemplateHelper = async (id) => {
    NProgress.start();
    return await axiosInstance("/ats/deleteAgreementTemplate", {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            workspace: getDomain(),
            template_pk: id,
            id: id,
        },
    })
        .then((res) => {
            NProgress.done();
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            NProgress.done();
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

// ── Agreement Masters Helpers ────────────────────────────────────────────────
const getMasterRoute = (action, type) => {
    const map = {
        'commission': 'Commissions',
        'fee_basis': 'FeeBasis',
        'agreement_duration': 'Durations',
        'replacement_period': 'ReplacementPeriods',
        'header_image': 'HeaderImages',
        'footer_image': 'FooterImages',
        'agreement_type': 'Types'
    };
    return `/ats/${action}Agreement${map[type]}`;
};

export const GetAtsAgreementMastersHelper = async (masterType) => {
    NProgress.start();
    return await axiosInstance(getMasterRoute('get', masterType), {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            workspace: getDomain(),
            master_type: masterType,
        },
    })
        .then((res) => {
            NProgress.done();
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            NProgress.done();
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

export const AddAtsAgreementMasterHelper = async (masterType, name, value, extra) => {
    NProgress.start();
    return await axiosInstance(getMasterRoute('add', masterType), {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            workspace: getDomain(),
            master_type: masterType,
            name,
            value,
            ...extra,
        },
    })
        .then((res) => {
            NProgress.done();
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            NProgress.done();
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

export const UpdateAtsAgreementMasterHelper = async (masterType, id, name, value) => {
    NProgress.start();
    return await axiosInstance(getMasterRoute('update', masterType), {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            workspace: getDomain(),
            master_type: masterType,
            id,
            name,
            value,
        },
    })
        .then((res) => {
            NProgress.done();
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            NProgress.done();
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};

export const DeleteAtsAgreementMasterHelper = async (masterType, id) => {
    NProgress.start();
    return await axiosInstance(getMasterRoute('delete', masterType), {
        method: "POST",
        baseURL: HRMS_ATS_API_URL,
        data: {
            workspace: getDomain(),
            master_type: masterType,
            id,
        },
    })
        .then((res) => {
            NProgress.done();
            return { message: "Success", hasError: false, data: res?.data };
        })
        .catch((err) => {
            NProgress.done();
            return { message: "Error", hasError: true, data: err?.response?.data };
        });
};
